import {
  createCommodityEntry,
  createEmptyContainerRow,
  createEmptyHandlingUnitRow,
  type PackingListUnitDefaults,
} from "@RequestFormV2/defaults/packingListDefaults";
import type { GoodCustomsInformation, PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { isContainerRow } from "@RequestFormV2/sections/PackingList/utils/packingListSplit";

function cloneRows(rows: PackingListRow[]): PackingListRow[] {
  return rows.map((row) => ({
    ...row,
    good_customs_information: row.good_customs_information
      ? row.good_customs_information.map((entry) => ({ ...entry }))
      : row.good_customs_information,
  }));
}

function createEmptyCommodity(defaultCurrency = ""): GoodCustomsInformation {
  return createCommodityEntry({}, defaultCurrency);
}

export function ensureCommodityKeys(rows: PackingListRow[]): PackingListRow[] {
  return rows.map((row) => {
    if (!row.good_customs_information?.length) return row;

    let rowChanged = false;
    const commodities = row.good_customs_information.map((commodity) => {
      if (commodity.commodity_key) return commodity;
      rowChanged = true;
      return { ...commodity, commodity_key: crypto.randomUUID() };
    });

    return rowChanged ? { ...row, good_customs_information: commodities } : row;
  });
}

export function addContainerRow(rows: PackingListRow[], unitDefaults?: PackingListUnitDefaults): PackingListRow[] {
  return [...rows, createEmptyContainerRow(unitDefaults)];
}

export function addHandlingUnitRow(rows: PackingListRow[], unitDefaults?: PackingListUnitDefaults): PackingListRow[] {
  return [...rows, createEmptyHandlingUnitRow(unitDefaults)];
}

export function ensureCommodityOnRow(
  rows: PackingListRow[],
  packingListIndex: number,
  defaultCurrency = "",
): PackingListRow[] {
  const nextRows = cloneRows(rows);
  const row = nextRows[packingListIndex];
  if (!row) return rows;

  const commodities = row.good_customs_information ?? [];
  if (!commodities.length) {
    row.good_customs_information = [createEmptyCommodity(defaultCurrency)];
  }

  return nextRows;
}

export function addCommodityToRow(rows: PackingListRow[], packingListIndex: number, defaultCurrency = ""): PackingListRow[] {
  const nextRows = cloneRows(rows);
  const row = nextRows[packingListIndex];
  if (!row) return rows;

  const commodities = [...(row.good_customs_information ?? [])];
  if (!commodities.length) {
    row.good_customs_information = [createEmptyCommodity(defaultCurrency)];
    return nextRows;
  }

  commodities.push(createEmptyCommodity(defaultCurrency));
  row.good_customs_information = commodities;

  return nextRows;
}

export function duplicateCommodityInRow(
  rows: PackingListRow[],
  packingListIndex: number,
  commodityIndex: number,
): PackingListRow[] {
  const nextRows = cloneRows(rows);
  const row = nextRows[packingListIndex];
  if (!row) return rows;

  const commodities = [...(row.good_customs_information ?? [])];
  const commodity = commodities[commodityIndex];
  if (!commodity) return rows;

  commodities.splice(commodityIndex + 1, 0, {
    ...commodity,
    commodity_key: crypto.randomUUID(),
    parent_assigned: commodity.parent_assigned ?? false,
  });
  row.good_customs_information = commodities;

  return nextRows;
}

export function deleteCommodityFromRow(
  rows: PackingListRow[],
  packingListIndex: number,
  commodityIndex: number,
): PackingListRow[] {
  const nextRows = cloneRows(rows);
  const row = nextRows[packingListIndex];
  if (!row) return rows;

  const commodities = [...(row.good_customs_information ?? [])];
  commodities.splice(commodityIndex, 1);
  row.good_customs_information = commodities.length ? commodities : undefined;

  return nextRows;
}

export function assignCommodityToParent(
  rows: PackingListRow[],
  packingListIndex: number,
  commodityIndex: number,
  toPackingListIndex: number,
): PackingListRow[] {
  const nextRows = cloneRows(rows);
  const row = nextRows[packingListIndex];
  if (!row) return rows;

  const commodities = [...(row.good_customs_information ?? [])];
  const commodity = commodities[commodityIndex];
  if (!commodity) return rows;

  commodities[commodityIndex] = {
    ...commodity,
    parent_assigned: true,
    parent_packing_list_index: toPackingListIndex !== packingListIndex ? toPackingListIndex : undefined,
  };
  row.good_customs_information = commodities;

  return nextRows;
}

export function hasAnyCommodityRows(rows: PackingListRow[]): boolean {
  return rows.some((row) => (row.good_customs_information?.length ?? 0) > 0);
}

export function ensureInitialCommodity(rows: PackingListRow[], defaultCurrency = ""): PackingListRow[] {
  if (hasAnyCommodityRows(rows)) return rows;

  const targetIndex = resolveCommodityTargetIndex(rows);
  if (targetIndex === undefined) return rows;

  return addCommodityToRow(rows, targetIndex, defaultCurrency);
}

export function resolveCommodityTargetIndex(rows: PackingListRow[]): number | undefined {
  if (!rows.length) return undefined;

  for (let index = rows.length - 1; index >= 0; index -= 1) {
    if ((rows[index].good_customs_information?.length ?? 0) > 0) {
      return index;
    }
  }

  const firstContainerIndex = rows.findIndex((row) => isContainerRow(row));
  if (firstContainerIndex !== -1) return firstContainerIndex;

  const firstTypedHandlingUnitIndex = rows.findIndex((row) => !isContainerRow(row) && Boolean(row.handling_unit));
  if (firstTypedHandlingUnitIndex !== -1) return firstTypedHandlingUnitIndex;

  const firstHandlingUnitIndex = rows.findIndex((row) => !isContainerRow(row));
  return firstHandlingUnitIndex === -1 ? undefined : firstHandlingUnitIndex;
}
